/**
 * Type definitions for authentication context.
 * Extracted from AuthContext.tsx for reusability and maintainability.
 */

import type { ApiResponseEnvelope } from '@/services/api'
import type { AvatarConfig } from '@/types/avatar'

/**
 * User object representing an authenticated user.
 */
export interface User {
    id: string
    username: string
    display_name?: string | null
    email: string
    avatar_url?: string | null
    avatar_updated_at?: string | null
    avatar_config?: AvatarConfig | null
    user_lang: string
    target_langs: string[]
    has_active_subscription?: boolean
}

/**
 * Token pair returned by the login endpoint.
 */
export interface AuthTokenPair {
    access_token: string
    refresh_token: string
}

/**
 * Authentication status states.
 */
export type AuthStatus = 'signedOut' | 'signedIn' | 'restoring'

/**
 * Language settings update payload.
 */
export interface LanguageSettings {
    user_lang?: string
    target_langs: string[]
    primary_target_lang: string
}

/**
 * API error response structure.
 */
export interface ApiErrorResponse {
    message?: string
    translation_key?: string
    translationKey?: string
    error?: string
    code?: string
}

/**
 * API user response structure.
 */
export interface ApiUserResponse {
    id: string
    username: string
    display_name?: string | null
    email: string
    avatar_url?: string | null
    avatar_updated_at?: string | null
    avatar_config?: AvatarConfig | null
    user_lang?: string | null
    target_langs?: string[]
    has_active_subscription?: boolean
}

/**
 * API authentication response structure.
 */
export interface ApiAuthResponse {
    access_token?: string
    refresh_token?: string
    user?: ApiUserResponse
}

/**
 * AuthError class to preserve translation keys from API error responses.
 */
export class AuthError extends Error {
    constructor(
        message: string,
        public translationKey?: string,
        public status?: number,
        public code?: string
    ) {
        super(message)
        this.name = 'AuthError'
    }
}

/**
 * Unwrap API response envelope: {data, meta} -> data
 */
export function unwrapApiResponse<T>(response: ApiResponseEnvelope<T>): T {
    if (response && typeof response === 'object' && 'data' in response) {
        return (response as { data: T }).data
    }
    return response
}

/**
 * Extract translation key from error response.
 */
export function extractTranslationKey(response: ApiErrorResponse): string | undefined {
    return response.translation_key || response.translationKey
}

/**
 * Normalize user data from API response.
 * - Filters target_langs to supported codes
 * - Forces user_lang to 'en'
 * - Ensures display_name is never null/empty
 *
 * @param userData - Raw user data from API
 * @param supportedCodes - Array of supported target language codes
 * @returns Normalized User object
 */
export function normalizeUserData(userData: ApiUserResponse, supportedCodes: string[]): User {
    // Normalize target_langs with filtering
    const targetLangs =
        Array.isArray(userData.target_langs) && userData.target_langs.length > 0
            ? userData.target_langs
            : []

    // Filter to supported target language codes
    const filtered = targetLangs.filter((code: string) => supportedCodes.includes(code))

    return {
        ...userData,
        user_lang: 'en', // Force user language to English
        target_langs: filtered,
        display_name: userData.display_name || userData.username, // Ensure display_name is never null/empty
        avatar_config: userData.avatar_config ?? null, // Preserve avatar_config
    }
}
